import IColumnName from "../types/IColumnName";

export const TABLE_PAGE_SIZES = [20, 50, 100];
export const DEFAULT_TABLE_PAGE_SIZE = 20;

export const TableIds = {
  contacts: "contacts",
  donations: "donations",
  users: "users",
  segments: "segments",
  simpleTextingKeywords: "simpleTextingKeywords",
  segmentDonors: "segmentDonors",
  keywordDonors: "keywordDonors",
  superadminDonations: "superadminDonations",
  urlManager: "urlManager",
} as const;

type TablePrefs = {
  columnDisabled?: Record<string, boolean>;
  pageSize?: number;
};

function storageKey(tableId: string): string {
  return `viewspark.tablePrefs.${tableId}`;
}

function readPrefs(tableId: string): TablePrefs {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(storageKey(tableId));
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as TablePrefs;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePrefs(tableId: string, patch: TablePrefs): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  const prev = readPrefs(tableId);
  const next: TablePrefs = {
    ...prev,
    ...patch,
    columnDisabled: patch.columnDisabled
      ? { ...(prev.columnDisabled || {}), ...patch.columnDisabled }
      : prev.columnDisabled,
  };
  try {
    window.localStorage.setItem(storageKey(tableId), JSON.stringify(next));
  } catch {
    return;
  }
}

/**
 * Overlay saved column visibility onto a table's current column list.
 */
export function applyColumnPrefs<T extends IColumnName>(
  tableId: string,
  columns: T[]
): T[] {
  const saved = readPrefs(tableId).columnDisabled;
  if (!saved) {
    return columns;
  }
  return columns.map(col => {
    if (Object.prototype.hasOwnProperty.call(saved, col.key)) {
      return { ...col, isDisabled: saved[col.key] };
    }
    return { ...col };
  });
}

/**
 * True when the user has saved this column as visible.
 */
export function wasColumnExplicitlyShown(tableId: string, key: string): boolean {
  const saved = readPrefs(tableId).columnDisabled;
  return Boolean(saved && Object.prototype.hasOwnProperty.call(saved, key) && saved[key] === false);
}

/**
 * Persist one column's visibility without writing auto-hidden columns.
 */
export function saveColumnPref(
  tableId: string,
  key: string,
  isDisabled: boolean
): void {
  writePrefs(tableId, { columnDisabled: { [key]: isDisabled } });
}

/**
 * Apply saved prefs, toggle one column, persist that key, return the new list.
 */
export function toggleSavedColumn<T extends IColumnName>(
  tableId: string,
  columns: T[],
  index: number
): T[] {
  const copy = applyColumnPrefs(tableId, columns);
  if (!copy[index]) {
    return copy;
  }
  copy[index] = { ...copy[index], isDisabled: !copy[index].isDisabled };
  saveColumnPref(tableId, copy[index].key, copy[index].isDisabled);
  return copy;
}

export function getTablePageSize(tableId: string): number {
  const size = readPrefs(tableId).pageSize;
  if (typeof size === "number" && TABLE_PAGE_SIZES.indexOf(size) !== -1) {
    return size;
  }
  return DEFAULT_TABLE_PAGE_SIZE;
}

export function saveTablePageSize(tableId: string, pageSize: number): void {
  if (TABLE_PAGE_SIZES.indexOf(pageSize) === -1) {
    return;
  }
  writePrefs(tableId, { pageSize });
}
